/**
 * Main Application Entry
 * Sets up providers and router with Sentry error boundary
 */
import React from 'react';
import { RouterProvider } from 'react-router/dom';
import * as Sentry from "@sentry/react";
import { logger } from './lib/logger';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider } from './contexts/AuthContext';
import { BillingProvider } from './contexts/BillingContext';
import { ExpertModeProvider } from './contexts/ExpertModeContext';
import { router } from './router';

// Fallback component shown when an error is caught
function ErrorFallback({ error, resetError }: { error: Error; resetError: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#050505]">
      <div className="text-center max-w-lg px-6">
        <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
          <svg className="w-10 h-10 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          Something went wrong
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
          An unexpected error occurred. Our team has been notified and is working on a fix.
        </p>
        <div className="space-y-4">
          <button
            onClick={resetError}
            className="w-full px-4 py-2 bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-lg font-medium transition-colors"
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.href = '/'}
            className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-900 dark:text-white rounded-lg font-medium transition-colors"
          >
            Go Home
          </button>
        </div>
        {process.env.NODE_ENV === 'development' && (
          <details className="mt-6 text-left">
            <summary className="text-sm text-gray-500 cursor-pointer">Error details</summary>
            <pre className="mt-2 p-3 bg-gray-100 dark:bg-gray-800 rounded text-xs text-red-600 dark:text-red-400 overflow-auto">
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

export default function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => <ErrorFallback error={error} resetError={resetError} />}
      onError={(error) => {
        // Additional error logging if needed
        logger.error('Caught error:', error);
        Sentry.captureException(error);
      }}
    >
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <BillingProvider>
              <ExpertModeProvider>
                <RouterProvider router={router} />
              </ExpertModeProvider>
            </BillingProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  );
}
